/**
 * 3Dセキュア認証処理モジュール（Web版）
 * APIサーバー経由でZEUS 3DS APIを呼び出す
 */

import { getAppConfig } from "../config";
import { getFirebaseUser } from "../firebase";
import {
    THREE_DS_ERROR_CODES,
    handle3DSError
} from "../generated/lib-domain/zeus3dSecure";
import type {
    PostThreeDSecureEnrollRequestBody,
    PostThreeDSecureEnrollResponseBody,
    PostThreeDSecurePayRequestBody,
    PostThreeDSecurePayResponseBody,
    PostThreeDSecureQuickEnrollRequestBody,
    PostThreeDSecureQuickEnrollResponseBody,
    PostThreeDSecureQuickPayRequestBody,
    PostThreeDSecureQuickPayResponseBody
} from "../generated/UserAPIAgent";

/**
 * 共通のAPI呼び出し処理
 */
async function callThreeDSecureAPI<TRequest, TResponse>(
    endpoint: string,
    body: TRequest
): Promise<TResponse> {
    const config = getAppConfig();
    const user = await getFirebaseUser();

    if (!user) {
        throw new Error("User not authenticated");
    }

    const idToken = await user.getIdToken();
    const url = `${config.apiBaseUrl}${endpoint}`;

    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(body)
    });

    const data = (await response.json()) as TResponse;

    // HTTP 400の場合でもJSONレスポンスが返されるので、それをそのまま返す
    // （success=falseのレスポンスにerrorCodeとerrorMessageが含まれる）
    return data;
}

/**
 * 3DS認証開始（初回決済）
 * @param body 認証開始リクエスト
 * @returns 認証開始レスポンス（iframeUrl等）
 */
export async function startThreeDSecureEnroll(
    body: PostThreeDSecureEnrollRequestBody
): Promise<PostThreeDSecureEnrollResponseBody> {
    return await callThreeDSecureAPI<
        PostThreeDSecureEnrollRequestBody,
        PostThreeDSecureEnrollResponseBody
    >("/zeus/3ds/enroll", body);
}

/**
 * 3DS認証開始（継続決済/QuickCharge）
 * @param body 認証開始リクエスト（sendid含む）
 * @returns 認証開始レスポンス（iframeUrl等）
 */
export async function startThreeDSecureQuickEnroll(
    body: PostThreeDSecureQuickEnrollRequestBody
): Promise<PostThreeDSecureQuickEnrollResponseBody> {
    return await callThreeDSecureAPI<
        PostThreeDSecureQuickEnrollRequestBody,
        PostThreeDSecureQuickEnrollResponseBody
    >("/zeus/3ds/quick/enroll", body);
}

/**
 * 3DS決済実行（初回決済）
 * @param body 決済実行リクエスト（sessionId, transStatus等）
 * @returns 決済実行レスポンス（orderId等）
 */
export async function executeThreeDSecurePay(
    body: PostThreeDSecurePayRequestBody
): Promise<PostThreeDSecurePayResponseBody> {
    return await callThreeDSecureAPI<
        PostThreeDSecurePayRequestBody,
        PostThreeDSecurePayResponseBody
    >("/zeus/3ds/pay", body);
}

/**
 * 3DS決済実行（継続決済/QuickCharge）
 * @param body 決済実行リクエスト（sessionId, transStatus, sendid等）
 * @returns 決済実行レスポンス（orderId等）
 */
export async function executeThreeDSecureQuickPay(
    body: PostThreeDSecureQuickPayRequestBody
): Promise<PostThreeDSecureQuickPayResponseBody> {
    return await callThreeDSecureAPI<
        PostThreeDSecureQuickPayRequestBody,
        PostThreeDSecureQuickPayResponseBody
    >("/zeus/3ds/quick/pay", body);
}

/**
 * エラーコード定数をエクスポート（コンポーネントで使用）
 */
export { THREE_DS_ERROR_CODES, handle3DSError };
